// Protocol Development API client. The auth token is attached globally by
// AuthContext (axios.defaults), so these are plain calls.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const base = `${API_URL}/api/v1/protocols`;

export const getProtocolConfig = () => axios.get(`${base}/config`).then((r) => r.data);
export const listProtocols = () => axios.get(base).then((r) => r.data);
export const createProtocol = (payload) => axios.post(base, payload).then((r) => r.data);
export const getProtocol = (id) => axios.get(`${base}/${id}`).then((r) => r.data);
export const deleteProtocol = (id) => axios.delete(`${base}/${id}`).then((r) => r.data);

export const assistProtocol = (id, payload) =>
  axios.post(`${base}/${id}/assist`, payload).then((r) => r.data);

export const updateSection = (id, sectionId, payload) =>
  axios.put(`${base}/${id}/sections/${sectionId}`, payload).then((r) => r.data);
export const addSection = (id, payload) =>
  axios.post(`${base}/${id}/sections`, payload).then((r) => r.data);
export const deleteSection = (id, sectionId) =>
  axios.delete(`${base}/${id}/sections/${sectionId}`).then((r) => r.data);

export const addQuestion = (id, payload) =>
  axios.post(`${base}/${id}/questions`, payload).then((r) => r.data);
export const resolveQuestion = (id, qid, payload) =>
  axios.patch(`${base}/${id}/questions/${qid}`, payload).then((r) => r.data);

// Cursor-style chat: propose edits → Keep/Discard → Undo.
export const getChat = (id) => axios.get(`${base}/${id}/chat`).then((r) => r.data);
export const sendChat = (id, message) => axios.post(`${base}/${id}/chat`, { message }).then((r) => r.data);
// Dictate a chat message: returns the transcript for the user to review, not an AI reply.
export const transcribeChatVoice = (id, blob) => {
  const fd = new FormData();
  fd.append('audio', blob, 'voice-note.webm');
  return axios.post(`${base}/${id}/chat/transcribe`, fd).then((r) => r.data);
};
export const applyChat = (id, messageId, edits) =>
  axios.post(`${base}/${id}/chat/${messageId}/apply`, { edits }).then((r) => r.data);
export const discardChat = (id, messageId) =>
  axios.post(`${base}/${id}/chat/${messageId}/discard`).then((r) => r.data);
export const undoProtocol = (id) => axios.post(`${base}/${id}/undo`).then((r) => r.data);

// Open-question assignment workflow.
export const getAssignees = (id) => axios.get(`${base}/${id}/assignees`).then((r) => r.data);
export const assignQuestion = (id, qid, assigneeId) =>
  axios.post(`${base}/${id}/questions/${qid}/assign`, { assignee_id: assigneeId }).then((r) => r.data);
export const getComments = (id, qid) => axios.get(`${base}/${id}/questions/${qid}/comments`).then((r) => r.data);
export const addComment = (id, qid, body) =>
  axios.post(`${base}/${id}/questions/${qid}/comments`, { body }).then((r) => r.data);
export const updateComment = (id, qid, cid, body) =>
  axios.patch(`${base}/${id}/questions/${qid}/comments/${cid}`, { body }).then((r) => r.data);
export const deleteComment = (id, qid, cid) =>
  axios.delete(`${base}/${id}/questions/${qid}/comments/${cid}`).then((r) => r.data);
export const embedQuestion = (id, qid, payload) =>
  axios.post(`${base}/${id}/questions/${qid}/embed`, payload).then((r) => r.data);
export const declineQuestion = (id, qid, reason) =>
  axios.post(`${base}/${id}/questions/${qid}/decline`, { reason }).then((r) => r.data);

// Audit trail.
export const getHistory = (id) => axios.get(`${base}/${id}/history`).then((r) => r.data);

// Phase 3: synthesize, point-of-care, analytics.
export const synthesizeSource = (id, file) => {
  const fd = new FormData();
  fd.append('file', file, file.name);
  return axios.post(`${base}/${id}/synthesize`, fd).then((r) => r.data);
};
export const getPublished = () => axios.get(`${base}/published`).then((r) => r.data);
export const getUsage = (id) => axios.get(`${base}/${id}/usage`).then((r) => r.data);
export const attachProtocolToSession = (sessionId, protocolId) =>
  axios.put(`${API_URL}/api/v1/clinical/session/${sessionId}/protocol`, { protocol_id: protocolId || null }).then((r) => r.data);

export const submitProtocol = (id, reviewerId) =>
  axios.post(`${base}/${id}/submit`, { reviewer_id: reviewerId || null }).then((r) => r.data);
export const returnToDraft = (id) => axios.post(`${base}/${id}/return-to-draft`).then((r) => r.data);
export const approveProtocol = (id) => axios.post(`${base}/${id}/approve`).then((r) => r.data);
export const newVersion = (id) => axios.post(`${base}/${id}/new-version`).then((r) => r.data);

// Version-level review comments (with @mentions).
export const getReviewComments = (id) => axios.get(`${base}/${id}/comments`).then((r) => r.data);
export const addReviewComment = (id, body, mentions) =>
  axios.post(`${base}/${id}/comments`, { body, mentions: mentions || [] }).then((r) => r.data);

// Point-of-care checklist — the questions to ask during an encounter.
// Authoring side (workbench):
export const getChecklist = (id) => axios.get(`${base}/${id}/checklist`).then((r) => r.data);
export const generateChecklist = (id) => axios.post(`${base}/${id}/checklist/generate`).then((r) => r.data);
export const addChecklistItem = (id, payload) =>
  axios.post(`${base}/${id}/checklist`, payload).then((r) => r.data);
export const updateChecklistItem = (id, itemId, payload) =>
  axios.patch(`${base}/${id}/checklist/${itemId}`, payload).then((r) => r.data);
export const deleteChecklistItem = (id, itemId) =>
  axios.delete(`${base}/${id}/checklist/${itemId}`).then((r) => r.data);

// Encounter side (recording sidebar):
export const getSessionChecklist = (sessionId) =>
  axios.get(`${base}/session/${sessionId}/checklist`).then((r) => r.data);
export const markChecklistItem = (sessionId, itemId, covered) =>
  axios.put(`${base}/session/${sessionId}/checklist/${itemId}`, { covered }).then((r) => r.data);
// Post one clip of the live encounter; `context` is the previous clip's transcript
// so an answer straddling the boundary still registers.
export const observeChecklist = (sessionId, blob, context) => {
  const fd = new FormData();
  fd.append('chunk', blob, 'clip.webm');
  fd.append('context', context || '');
  return axios.post(`${base}/session/${sessionId}/checklist/observe`, fd).then((r) => r.data);
};

// Capture mode — chunked long-session upload.
export const recordingStart = (id) => axios.post(`${base}/${id}/recording/start`).then((r) => r.data);
export const recordingChunk = (id, seq, blob) => {
  const fd = new FormData();
  fd.append('chunk', blob, `chunk-${seq}.webm`);
  return axios.put(`${base}/${id}/recording/chunk?seq=${seq}`, fd).then((r) => r.data);
};
export const recordingFinalize = (id) => axios.post(`${base}/${id}/recording/finalize`).then((r) => r.data);
